import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../components/auth/AuthContext";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  IconButton,
  Divider,
  Container,
  AppBar,
  Toolbar,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Badge,
  useMediaQuery,
  useTheme,
  Paper,
  Chip,
  
} from "@mui/material";
import {
  School as SchoolIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  Event as EventIcon,
  Schedule as ScheduleIcon,
  Edit as EditIcon,
  NewReleases as NewsIcon,
  Bolt as BoltIcon,
  Public as PublicIcon,
  Workspaces as WorkspacesIcon,
  Assessment as AssessmentIcon,
  Forum as ForumIcon,
  Menu as MenuIcon,
  Home as HomeIcon,
  Star as StarIcon,
  Notifications as NotificationsIcon,
  AccountCircle as AccountCircleIcon,
  Class as ClassIcon,
  Book as BookIcon,
  Groups as GroupsIcon,
  Assignment as AssignmentIcon,
  EmojiEvents as EmojiEventsIcon,
  Dashboard as DashboardIcon,
  BarChart as BarChartIcon,
  Settings as SettingsIcon,
  ExitToApp as ExitToAppIcon,
   GroupWork as AlumniIcon,
} from "@mui/icons-material";
import axios from "axios";
import AOS from "aos";
import "aos/dist/aos.css";

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { schoolName, schoolId } = useAuth();
  const [learners, setLearners] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [parents, setParents] = useState([]);
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nationalEvents, setNationalEvents] = useState([]);
  const [scholarships, setScholarships] = useState([]);
  const [scheduledExams, setScheduledExams] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: "ease-in-out",
      once: true,
    });
  }, []);

  const getTimeBasedGreeting = () => {
    const hour = new Date().getHours();
    return hour < 12
      ? "Good Morning"
      : hour < 17
      ? "Good Afternoon"
      : "Good Evening";
  };

  const calculateTimeRemaining = (endDate) => {
    const now = new Date();
    const end = new Date(endDate);
    const diff = end - now;

    if (diff <= 0) return "Expired";

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    return `${days}d ${hours}h ${minutes}m ${seconds}s`;
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [
        learnersRes,
        teachersRes,
        parentsRes,
        eventsRes,
        newsRes,
        nationalEventsRes,
        scholarshipsRes,
        scheduledExamsRes,
      ] = await Promise.all([
        axios.get("https://kidsm.vercel.app/api/schools/learners", {
          params: {
            schoolId,
            fields:
              "fullName admissionNumber parentName parentEmail parentContact parentId schoolId class _id",
          },
        }),
        axios.get("https://kidsm.vercel.app/api/schools/teachers", {
          params: { schoolId },
        }),
        axios.get("https://kidsm.vercel.app/api/schools/parents", {
          params: { schoolId },
        }),
        axios.get("https://kidsm.vercel.app/api/schools/events", {
          params: { schoolId },
        }),
        axios.get(`https://kidsm.vercel.app/api/schools/news/${schoolId}`),
        axios.get(
          "https://kidsm.vercel.app/api/schools/latest-amplified-events"
        ),
        axios.get("https://kidsm.vercel.app/api/schools/latest-scholarships"),
        axios.get("https://kidsm.vercel.app/api/schools/scheduled-exams", {
          params: { schoolId },
        }),
      ]);

      setLearners(learnersRes.data || []);
      setTeachers(teachersRes.data || []);
      setParents(parentsRes.data || []);
      setEvents(eventsRes.data || []);
      setNews(newsRes.data || []);
      setNationalEvents(nationalEventsRes.data || []);
      setScholarships(scholarshipsRes.data || []);
      setScheduledExams(
        (scheduledExamsRes.data.exams || []).map((exam) => ({
          ...exam,
          timeRemaining: calculateTimeRemaining(exam.endDate),
        }))
      );
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => {
      setScheduledExams((prevExams) =>
        prevExams.map((exam) => ({
          ...exam,
          timeRemaining: calculateTimeRemaining(exam.endDate),
        }))
      );
    }, 1000);
    return () => clearInterval(interval);
  }, [schoolId]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const groupLearnersByClass = (learners) => {
    return learners.reduce((acc, learner) => {
      const { class: className } = learner;
      if (!acc[className]) acc[className] = [];
      acc[className].push(learner);
      return acc;
    }, {});
  };

  const learnersByClass = groupLearnersByClass(learners);

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.9, y: 30 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
    hover: { scale: 1.05, transition: { duration: 0.2 } },
  };

  const statVariants = {
    pulse: {
      scale: [1, 1.05, 1],
      transition: { repeat: Infinity, duration: 2 },
    },
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <Box sx={{ width: 250, bgcolor: "background.paper", height: "100%" }}>
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#6200ee",
          color: "white",
        }}
      >
        <SchoolIcon sx={{ mr: 1, fontSize: 32 }} />
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          {schoolName}
        </Typography>
      </Box>
      <Divider />
      <List>
        {[
          { text: "Dashboard", icon: <DashboardIcon />, path: "/" },
          { text: "Students", icon: <PersonIcon />, path: "/all-students" },
          { text: "Teachers", icon: <GroupsIcon />, path: "/all-teachers" },
          { text: "Parents", icon: <PeopleIcon />, path: "/all-parents" },
          { 
      text: "Alumni", 
      icon: <AlumniIcon />, 
      path: "/alumni-management",
      state: { schoolId } 
    },
          {
            text: "Modify Results",
            icon: <EditIcon />,
            path: "/modify-class-results",
            state: { schoolId },
          },
          {
            text: "School Services",
            icon: <BookIcon />,
            path: "/school-services",
            state: { schoolId },
          },
          { text: "Exams", icon: <AssignmentIcon />, path: "/schedule-exam" },
          {
            text: "Events",
            icon: <EventIcon />,
            path: "/all-events",
            state: { schoolId, userType: "institution" },
          },
          {
            text: "News",
            icon: <NewsIcon />,
            path: "/all-news",
            state: { schoolId },
          },
          {
            text: "Reports",
            icon: <BarChartIcon />,
            path: "/school-performance",
          },
          {
            text: "School Network",
            icon: <ForumIcon />,
            path: "/school-interactions",
          },
          {
            text: "Settings",
            icon: <SettingsIcon />,
            path: "/school-profile",
            state: { schoolId },
          },
          { text: "Logout", icon: <ExitToAppIcon />, path: "/logout" },
        ].map((item, index) => (
          <ListItem
            button
            key={index}
            onClick={() => navigate(item.path, { state: item.state || {} })}
            sx={{
              "&:hover": { bgcolor: "#f5f5f5" },
              borderRadius: 1,
              mx: 1,
              my: 0.5,
            }}
          >
            <ListItemIcon sx={{ color: "#6200ee" }}>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{ fontWeight: "medium" }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  if (loading && !refreshing) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        }}
      >
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        >
          <img
            src="https://cdn-icons-png.flaticon.com/512/477/477103.png"
            alt="Loading"
            style={{ width: 80, height: 80, filter: "hue-rotate(200deg)" }}
          />
        </motion.div>
        <Typography
          variant="h6"
          sx={{
            mt: 3,
            color: "#2c3e50",
            fontWeight: "bold",
            background: "linear-gradient(to right, #6200ee, #03dac6)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Loading your school dashboard...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f8f9fa" }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          bgcolor: "white",
          color: "#2c3e50",
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: "none" }, color: "#6200ee" }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
            <SchoolIcon sx={{ color: "#6200ee", mr: 1, fontSize: 32 }} />
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                fontWeight: "bold",
                background: "linear-gradient(to right, #6200ee, #03dac6)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {schoolName}
            </Typography>
          </Box>

          <Box
            sx={{ display: { xs: "none", md: "flex" }, alignItems: "center" }}
          >
            <IconButton size="large" color="inherit" sx={{ color: "#6200ee" }}>
              <Badge badgeContent={notifications.length} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
            <IconButton
              size="large"
              edge="end"
              aria-label="account of current user"
              aria-haspopup="true"
              color="inherit"
              sx={{ color: "#6200ee" }}
              onClick={() =>
                navigate("/school-profile", { state: { schoolId } })
              }
            >
              <AccountCircleIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Sidebar Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: 250 }, flexShrink: { md: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": { boxSizing: "border-box", width: 250 },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: 250,
              borderRight: "none",
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - 250px)` },
          marginTop: "64px",
          bgcolor: "#f8f9fa",
        }}
      >
        <Container maxWidth="xl" sx={{ py: 2 }}>
          {/* Greeting Section */}
          <Box sx={{ mb: 4 }} data-aos="fade-down">
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: "bold",
                color: "#2c3e50",
                mb: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <span
                style={{
                  background: "linear-gradient(to right, #6200ee, #03dac6)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {getTimeBasedGreeting()}, {schoolName}
              </span>
              👋
            </Typography>
            <Typography variant="subtitle1" sx={{ color: "#7f8c8d", mb: 2 }}>
              Here's what's happening with your school today
            </Typography>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="contained"
                onClick={handleRefresh}
                disabled={refreshing}
                startIcon={<BoltIcon />}
                sx={{
                  bgcolor: "#6200ee",
                  "&:hover": { bgcolor: "#7f39fb" },
                  borderRadius: "10px",
                  boxShadow: "0 4px 6px rgba(98, 0, 238, 0.2)",
                  textTransform: "none",
                  px: 3,
                  py: 1,
                }}
              >
                {refreshing ? "Refreshing..." : "Refresh Dashboard"}
              </Button>
            </Box>
          </Box>

          {/* Quick Stats */}
          <Grid container spacing={3} sx={{ mb: 4 }} data-aos="fade-up">
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 2,
                  p: 2,
                  bgcolor: "white",
                  borderRadius: "10px",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
                }}
              >
                <SchoolIcon sx={{ color: "#6200ee", mr: 1, fontSize: 28 }} />
                <Typography
                  variant="h5"
                  sx={{ fontWeight: "bold", color: "#2c3e50" }}
                >
                  Quick Stats
                </Typography>
              </Box>
              <Grid container spacing={2}>
                {[
                  {
                    icon: <SchoolIcon sx={{ fontSize: 30 }} />,
                    count: learners.length,
                    label: "Students",
                    color: "#4CAF50",
                    bgColor: "rgba(76, 175, 80, 0.1)",
                  },
                  {
                    icon: <PersonIcon sx={{ fontSize: 30 }} />,
                    count: teachers.length,
                    label: "Teachers",
                    color: "#2196F3",
                    bgColor: "rgba(33, 150, 243, 0.1)",
                  },
                  {
                    icon: <PeopleIcon sx={{ fontSize: 30 }} />,
                    count: parents.length,
                    label: "Parents",
                    color: "#FF9800",
                    bgColor: "rgba(255, 152, 0, 0.1)",
                  },
                  {
                    icon: <EventIcon sx={{ fontSize: 30 }} />,
                    count: events.length,
                    label: "Events",
                    color: "#9C27B0",
                    bgColor: "rgba(156, 39, 176, 0.1)",
                  },
                ].map((stat, index) => (
                  <Grid item xs={12} sm={6} md={3} key={index}>
                    <motion.div variants={statVariants} animate="pulse">
                      <Card
                        sx={{
                          textAlign: "center",
                          p: 3,
                          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                          bgcolor: stat.bgColor,
                          borderRadius: "12px",
                          borderLeft: `4px solid ${stat.color}`,
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                        }}
                      >
                        <Box
                          sx={{
                            color: stat.color,
                            mb: 2,
                            display: "inline-flex",
                            p: 1.5,
                            bgcolor: "white",
                            borderRadius: "50%",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                          }}
                        >
                          {stat.icon}
                        </Box>
                        <Typography
                          variant="h3"
                          sx={{
                            fontWeight: "bold",
                            color: "#2c3e50",
                            mb: 0.5,
                          }}
                        >
                          {stat.count}
                        </Typography>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            color: "#7f8c8d",
                            fontWeight: "medium",
                          }}
                        >
                          {stat.label}
                        </Typography>
                      </Card>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>

          {/* Main Content Grid */}
          <Grid container spacing={3}>
            {/* First Row - Exams and Students */}
            <Grid item xs={12} lg={8} data-aos="fade-right">
              {/* Scheduled Exams */}
              <Card
                sx={{
                  mb: 3,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                  borderRadius: "12px",
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 2,
                      justifyContent: "space-between",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <ScheduleIcon
                        sx={{
                          color: "white",
                          mr: 1,
                          bgcolor: "#6200ee",
                          p: 1,
                          borderRadius: "8px",
                          fontSize: 28,
                        }}
                      />
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: "bold", color: "#2c3e50" }}
                      >
                        Scheduled Exams
                      </Typography>
                    </Box>
                    <IconButton
                      color="primary"
                      onClick={() =>
                        navigate("/schedule-exam", { state: { schoolId } })
                      }
                      sx={{
                        bgcolor: "#6200ee",
                        color: "white",
                        "&:hover": { bgcolor: "#7f39fb" },
                        borderRadius: "8px",
                      }}
                    >
                      <ScheduleIcon />
                    </IconButton>
                  </Box>

                  {scheduledExams.length === 0 ? (
                    <Box
                      sx={{
                        textAlign: "center",
                        p: 4,
                        bgcolor: "#f8f9fa",
                        borderRadius: "8px",
                      }}
                    >
                      <img
                        src="https://cdn-icons-png.flaticon.com/512/4076/4076478.png"
                        alt="No exams"
                        style={{ width: 100, opacity: 0.7, marginBottom: 16 }}
                      />
                      <Typography sx={{ color: "#666" }}>
                        No scheduled exams yet
                      </Typography>
                      <Button
                        variant="contained"
                        sx={{ mt: 2, bgcolor: "#6200ee" }}
                        onClick={() =>
                          navigate("/schedule-exam", { state: { schoolId } })
                        }
                      >
                        Schedule an Exam
                      </Button>
                    </Box>
                  ) : (
                    <Grid container spacing={2}>
                      {scheduledExams.map((exam, index) => (
                        <Grid item xs={12} sm={6} key={exam._id}>
                          <motion.div
                            variants={cardVariants}
                            initial="hidden"
                            animate="visible"
                            whileHover="hover"
                          >
                            <Card
                              sx={{
                                p: 2,
                                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                                bgcolor: "#fff",
                                borderRadius: "10px",
                                borderLeft: `4px solid ${
                                  index % 2 === 0 ? "#6200ee" : "#03dac6"
                                }`,
                                cursor: "pointer",
                              }}
                              onClick={() =>
                                navigate("/scheduled-exam-details", {
                                  state: { scheduledExam: exam, schoolId },
                                })
                              }
                            >
                              <CardContent>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    mb: 1,
                                  }}
                                >
                                  <EventIcon
                                    sx={{
                                      color:
                                        index % 2 === 0 ? "#6200ee" : "#03dac6",
                                      mr: 1,
                                    }}
                                  />
                                  <Typography
                                    variant="h6"
                                    sx={{ fontWeight: "600", color: "#2c3e50" }}
                                  >
                                    {exam.examType}
                                    {exam.examName
                                      ? `: ${exam.examName}`
                                      : ""}{" "}
                                    ({exam.term})
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    mb: 1,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "#666",
                                      display: "flex",
                                      alignItems: "center",
                                      mr: 2,
                                    }}
                                  >
                                    <span
                                      style={{
                                        display: "inline-block",
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        bgcolor: "#4CAF50",
                                        mr: 1,
                                      }}
                                    ></span>
                                    Start:{" "}
                                    {new Date(
                                      exam.startDate
                                    ).toLocaleDateString()}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "#666",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <span
                                      style={{
                                        display: "inline-block",
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        bgcolor: "#F44336",
                                        mr: 1,
                                      }}
                                    ></span>
                                    End:{" "}
                                    {new Date(
                                      exam.endDate
                                    ).toLocaleDateString()}
                                  </Typography>
                                </Box>
                                <Chip
                                  label={`Time Remaining: ${exam.timeRemaining}`}
                                  color={
                                    exam.timeRemaining === "Expired"
                                      ? "error"
                                      : "primary"
                                  }
                                  size="small"
                                  sx={{ mt: 1 }}
                                />
                              </CardContent>
                            </Card>
                          </motion.div>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </CardContent>
              </Card>

              {/* Students Overview */}
              <Card
                sx={{
                  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                  borderRadius: "12px",
                }}
              >
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <SchoolIcon
                      sx={{
                        color: "white",
                        mr: 1,
                        bgcolor: "#6200ee",
                        p: 1,
                        borderRadius: "8px",
                        fontSize: 28,
                      }}
                    />
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: "bold", color: "#2c3e50" }}
                    >
                      Students Overview
                    </Typography>
                  </Box>

                  {Object.entries(learnersByClass)
                    .slice(0, 2)
                    .map(([className, learnersInClass]) => (
                      <Box key={className} sx={{ mb: 3 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 1.5,
                            p: 1,
                            bgcolor: "#f5f5f5",
                            borderRadius: "6px",
                          }}
                        >
                          <ClassIcon sx={{ color: "#6200ee", mr: 1 }} />
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: "600", color: "#333" }}
                          >
                            {className}
                          </Typography>
                          <Chip
                            label={`${learnersInClass.length} students`}
                            size="small"
                            sx={{
                              ml: "auto",
                              bgcolor: "#e3f2fd",
                              color: "#1976d2",
                            }}
                          />
                        </Box>

                        <Grid container spacing={2}>
                          {learnersInClass
                            .slice(0, isMobile ? 1 : 2)
                            .map((learner) => (
                              <Grid item xs={12} sm={6} key={learner._id}>
                                <motion.div
                                  variants={cardVariants}
                                  initial="hidden"
                                  animate="visible"
                                  whileHover="hover"
                                >
                                  <Card
                                    sx={{
                                      p: 2,
                                      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                                      bgcolor: "#fff",
                                      borderRadius: "10px",
                                      cursor: "pointer",
                                    }}
                                    onClick={() =>
                                      navigate("/student-details", {
                                        state: { learner },
                                      })
                                    }
                                  >
                                    <CardContent>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          mb: 2,
                                        }}
                                      >
                                        <Avatar
                                          sx={{
                                            bgcolor: "#6200ee",
                                            width: 48,
                                            height: 48,
                                            mr: 2,
                                          }}
                                        >
                                          {learner.fullName.charAt(0)}
                                        </Avatar>
                                        <Box>
                                          <Typography
                                            variant="subtitle1"
                                            sx={{
                                              fontWeight: "600",
                                              color: "#2c3e50",
                                            }}
                                          >
                                            {learner.fullName}
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            sx={{ color: "#666" }}
                                          >
                                            Adm: {learner.admissionNumber}
                                          </Typography>
                                        </Box>
                                      </Box>
                                      <Divider sx={{ my: 1 }} />
                                      <Typography
                                        variant="body2"
                                        sx={{ color: "#666", mb: 1 }}
                                      >
                                        <strong>Parent:</strong>{" "}
                                        {learner.parentName}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{ color: "#666" }}
                                      >
                                        <strong>Contact:</strong>{" "}
                                        {learner.parentContact}
                                      </Typography>
                                    </CardContent>
                                  </Card>
                                </motion.div>
                              </Grid>
                            ))}
                        </Grid>
                      </Box>
                    ))}

                  <Box sx={{ textAlign: "center", mt: 2 }}>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => navigate("/all-students")}
                      sx={{
                        borderRadius: "8px",
                        textTransform: "none",
                        px: 4,
                      }}
                    >
                      View All Students
                    </Button>
                  </Box>
                </CardContent>
              </Card>

              {/* Results Management */}
              <Card
                sx={{
                  mb: 3,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                  borderRadius: "12px",
                }}
              >
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <EditIcon
                      sx={{
                        color: "white",
                        mr: 1,
                        bgcolor: "#6200ee",
                        p: 1,
                        borderRadius: "8px",
                        fontSize: 28,
                      }}
                    />
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: "bold", color: "#2c3e50" }}
                    >
                      Results Management
                    </Typography>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <motion.div
                        variants={cardVariants}
                        initial="hidden"
                        animate="visible"
                        whileHover="hover"
                      >
                        <Card
                          sx={{
                            p: 2,
                            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                            bgcolor: "#fff",
                            borderRadius: "10px",
                            cursor: "pointer",
                            borderLeft: "4px solid #6200ee",
                          }}
                          onClick={() =>
                            navigate("/modify-class-results", {
                              state: { schoolId },
                            })
                          }
                        >
                          <CardContent>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 1,
                              }}
                            >
                              <EditIcon sx={{ color: "#6200ee", mr: 1 }} />
                              <Typography
                                variant="h6"
                                sx={{ fontWeight: "600", color: "#2c3e50" }}
                              >
                                Modify Class Results
                              </Typography>
                            </Box>
                            <Typography sx={{ color: "#666" }}>
                              Edit or delete existing results for a class
                            </Typography>
                          </CardContent>
                        </Card>
                      </motion.div>
                    </Grid>
                  </Grid>
                   {/* // In the Results Management section, add this card: */}
            <Grid item xs={12}>
              <motion.div
                variants={cardVariants}
                initial="hidden"
                animate="visible"
                whileHover="hover"
              >
                <Card
                  sx={{
                    p: 2,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                    bgcolor: "#fff",
                    borderRadius: "10px",
                    cursor: "pointer",
                    borderLeft: "4px solid #03dac6",
                  }}
                  onClick={() =>
                    navigate("/edit-class-learners", {
                      state: { schoolId },
                    })
                  }
                >
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <PeopleIcon sx={{ color: "#03dac6", mr: 1 }} />
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: "600", color: "#2c3e50" }}
                      >
                        Edit Class Learners
                      </Typography>
                    </Box>
                    <Typography sx={{ color: "#666" }}>
                      Edit learner details for an entire class
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
                </CardContent>
              </Card>
            </Grid>

           

            {/* Second Column - Quick Actions, Teachers, and Parents */}
            <Grid item xs={12} lg={4} data-aos="fade-left">
              {/* Quick Actions */}
              <Card
                sx={{
                  mb: 3,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                  borderRadius: "12px",
                }}
              >
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <BoltIcon
                      sx={{
                        color: "white",
                        mr: 1,
                        bgcolor: "#6200ee",
                        p: 1,
                        borderRadius: "8px",
                        fontSize: 28,
                      }}
                    />
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: "bold", color: "#2c3e50" }}
                    >
                      Quick Actions
                    </Typography>
                  </Box>

                  <Grid container spacing={2}>
                    {[
                      {
                        title: "Add Student",
                        icon: <PersonIcon sx={{ fontSize: 28 }} />,
                        path: "/register-learner",
                        color: "#4CAF50",
                        bgColor: "rgba(76, 175, 80, 0.1)",
                      },
                      {
                        title: "Add Teacher",
                        icon: <GroupsIcon sx={{ fontSize: 28 }} />,
                        path: "/register-teacher",
                        color: "#2196F3",
                        bgColor: "rgba(33, 150, 243, 0.1)",
                      },
                      {
                        title: "Add Parent",
                        icon: <PeopleIcon sx={{ fontSize: 28 }} />,
                        path: "/register-parent",
                        color: "#FF9800",
                        bgColor: "rgba(255, 152, 0, 0.1)",
                      },
                      {
                        title: "Performance",
                        icon: <AssessmentIcon sx={{ fontSize: 28 }} />,
                        path: "/school-performance",
                        color: "#9C27B0",
                        bgColor: "rgba(156, 39, 176, 0.1)",
                      },
                      {
                        title: "School Network",
                        icon: <ForumIcon sx={{ fontSize: 28 }} />,
                        path: "/school-interactions",
                        color: "#00BCD4",
                        bgColor: "rgba(0, 188, 212, 0.1)",
                      },
                      {
                        title: "Publish News",
                        icon: <NewsIcon sx={{ fontSize: 28 }} />,
                        path: "/publish-news",
                        color: "#FF5722",
                        bgColor: "rgba(255, 87, 34, 0.1)",
                      },
                    ].map((action, index) => (
                      <Grid item xs={12} sm={6} md={6} key={index}>
                        <motion.div
                          variants={cardVariants}
                          initial="hidden"
                          animate="visible"
                          whileHover="hover"
                        >
                          <Card
                            sx={{
                              p: 2,
                              boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                              bgcolor: action.bgColor,
                              borderRadius: "10px",
                              borderLeft: `4px solid ${action.color}`,
                              cursor: "pointer",
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              alignItems: "center",
                              textAlign: "center",
                            }}
                            onClick={() => navigate(action.path)}
                          >
                            <Box
                              sx={{
                                color: action.color,
                                mb: 1,
                                p: 1.5,
                                bgcolor: "white",
                                borderRadius: "50%",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                              }}
                            >
                              {action.icon}
                            </Box>
                            <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: "600", color: "#2c3e50" }}
                            >
                              {action.title}
                            </Typography>
                          </Card>
                        </motion.div>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>

              {/* Teachers */}
              <Card
                sx={{
                  mb: 3,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                  borderRadius: "12px",
                }}
              >
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <GroupsIcon
                      sx={{
                        color: "white",
                        mr: 1,
                        bgcolor: "#6200ee",
                        p: 1,
                        borderRadius: "8px",
                        fontSize: 28,
                      }}
                    />
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: "bold", color: "#2c3e50" }}
                    >
                      Teachers
                    </Typography>
                  </Box>

                  {teachers.length === 0 ? (
                    <Box
                      sx={{
                        textAlign: "center",
                        p: 3,
                        bgcolor: "#f8f9fa",
                        borderRadius: "8px",
                      }}
                    >
                      <img
                        src="https://cdn-icons-png.flaticon.com/512/3131/3131728.png"
                        alt="No teachers"
                        style={{ width: 100, opacity: 0.7, marginBottom: 16 }}
                      />
                      <Typography sx={{ color: "#666", mb: 2 }}>
                        No teachers registered yet
                      </Typography>
                      <Button
                        variant="contained"
                        sx={{ bgcolor: "#6200ee" }}
                        onClick={() => navigate("/register-teacher")}
                      >
                        Add Teacher
                      </Button>
                    </Box>
                  ) : (
                    <>
                      <Grid container spacing={2}>
                        {teachers.slice(0, isMobile ? 1 : 2).map((teacher) => (
                          <Grid item xs={12} key={teacher._id}>
                            <motion.div
                              variants={cardVariants}
                              initial="hidden"
                              animate="visible"
                              whileHover="hover"
                            >
                              <Card
                                sx={{
                                  p: 2,
                                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                                  bgcolor: "#fff",
                                  borderRadius: "10px",
                                  cursor: "pointer",
                                }}
                                onClick={() =>
                                  navigate("/teacher-details", {
                                    state: { teacher },
                                  })
                                }
                              >
                                <CardContent>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      mb: 2,
                                    }}
                                  >
                                    <Avatar
                                      sx={{
                                        bgcolor: "#2196F3",
                                        width: 48,
                                        height: 48,
                                        mr: 2,
                                      }}
                                    >
                                      {teacher.fullName.charAt(0)}
                                    </Avatar>
                                    <Box>
                                      <Typography
                                        variant="subtitle1"
                                        sx={{
                                          fontWeight: "600",
                                          color: "#2c3e50",
                                        }}
                                      >
                                        {teacher.fullName}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{ color: "#666" }}
                                      >
                                        {teacher.subjectSpecialization ||
                                          "Teacher"}
                                      </Typography>
                                    </Box>
                                  </Box>
                                  <Divider sx={{ my: 1 }} />
                                  <Typography
                                    variant="body2"
                                    sx={{ color: "#666" }}
                                  >
                                    <strong>Email:</strong>{" "}
                                    {teacher.email || "Not provided"}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: "#666", mt: 1 }}
                                  >
                                    <strong>Phone:</strong>{" "}
                                    {teacher.phoneNumber || "Not provided"}
                                  </Typography>
                                </CardContent>
                              </Card>
                            </motion.div>
                          </Grid>
                        ))}
                      </Grid>

                      <Box sx={{ textAlign: "center", mt: 2 }}>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() => navigate("/all-teachers")}
                          sx={{
                            borderRadius: "8px",
                            textTransform: "none",
                            px: 4,
                          }}
                        >
                          View All Teachers
                        </Button>
                      </Box>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Parents */}
              <Card
                sx={{
                  mb: 3,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                  borderRadius: "12px",
                }}
              >
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <PeopleIcon
                      sx={{
                        color: "white",
                        mr: 1,
                        bgcolor: "#6200ee",
                        p: 1,
                        borderRadius: "8px",
                        fontSize: 28,
                      }}
                    />
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: "bold", color: "#2c3e50" }}
                    >
                      Parents
                    </Typography>
                  </Box>

                  {parents.length === 0 ? (
                    <Box
                      sx={{
                        textAlign: "center",
                        p: 3,
                        bgcolor: "#f8f9fa",
                        borderRadius: "8px",
                      }}
                    >
                      <img
                        src="https://cdn-icons-png.flaticon.com/512/3131/3131728.png"
                        alt="No parents"
                        style={{ width: 100, opacity: 0.7, marginBottom: 16 }}
                      />
                      <Typography sx={{ color: "#666", mb: 2 }}>
                        No parents registered yet
                      </Typography>
                      <Button
                        variant="contained"
                        sx={{ bgcolor: "#6200ee" }}
                        onClick={() => navigate("/register-parent")}
                      >
                        Add Parent
                      </Button>
                    </Box>
                  ) : (
                    <>
                      <Grid container spacing={2}>
                        {parents.slice(0, isMobile ? 1 : 2).map((parent) => (
                          <Grid item xs={12} key={parent._id}>
                            <motion.div
                              variants={cardVariants}
                              initial="hidden"
                              animate="visible"
                              whileHover="hover"
                            >
                              <Card
                                sx={{
                                  p: 2,
                                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                                  bgcolor: "#fff",
                                  borderRadius: "10px",
                                  cursor: "pointer",
                                }}
                                onClick={() =>
                                  navigate("/parent-details", {
                                    state: { parent },
                                  })
                                }
                              >
                                <CardContent>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      mb: 2,
                                    }}
                                  >
                                    <Avatar
                                      sx={{
                                        bgcolor: "#FF9800",
                                        width: 48,
                                        height: 48,
                                        mr: 2,
                                      }}
                                    >
                                      {parent.parentName.charAt(0)}
                                    </Avatar>
                                    <Box>
                                      <Typography
                                        variant="subtitle1"
                                        sx={{
                                          fontWeight: "600",
                                          color: "#2c3e50",
                                        }}
                                      >
                                        {parent.parentName}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{ color: "#666" }}
                                      >
                                        ID: {parent.idNumber}
                                      </Typography>
                                    </Box>
                                  </Box>
                                  <Divider sx={{ my: 1 }} />
                                  <Typography
                                    variant="body2"
                                    sx={{ color: "#666", mb: 1 }}
                                  >
                                    <strong>Student:</strong>{" "}
                                    {parent.learnerName}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: "#666" }}
                                  >
                                    <strong>Admission:</strong>{" "}
                                    {parent.learnerAdmissionNumber}
                                  </Typography>
                                </CardContent>
                              </Card>
                            </motion.div>
                          </Grid>
                        ))}
                      </Grid>

                      <Box sx={{ textAlign: "center", mt: 2 }}>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() => navigate("/all-parents")}
                          sx={{
                            borderRadius: "8px",
                            textTransform: "none",
                            px: 4,
                          }}
                        >
                          View All Parents
                        </Button>
                      </Box>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Second Row - Events and News */}
            <Grid item xs={12} md={6} data-aos="fade-up">
              {/* Upcoming Events */}
              <Card
                sx={{
                  mb: 3,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                  borderRadius: "12px",
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 2,
                      justifyContent: "space-between",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <EventIcon
                        sx={{
                          color: "white",
                          mr: 1,
                          bgcolor: "#6200ee",
                          p: 1,
                          borderRadius: "8px",
                          fontSize: 28,
                        }}
                      />
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: "bold", color: "#2c3e50" }}
                      >
                        Upcoming Events
                      </Typography>
                    </Box>
                    <IconButton
                      color="primary"
                      onClick={() => navigate("/publish-event")}
                      sx={{
                        bgcolor: "#6200ee",
                        color: "white",
                        "&:hover": { bgcolor: "#7f39fb" },
                        borderRadius: "8px",
                      }}
                    >
                      <EventIcon />
                    </IconButton>
                  </Box>

                  {events.length === 0 ? (
                    <Box
                      sx={{
                        textAlign: "center",
                        p: 4,
                        bgcolor: "#f8f9fa",
                        borderRadius: "8px",
                      }}
                    >
                      <img
                        src="https://cdn-icons-png.flaticon.com/512/3652/3652191.png"
                        alt="No events"
                        style={{ width: 100, opacity: 0.7, marginBottom: 16 }}
                      />
                      <Typography sx={{ color: "#666", mb: 2 }}>
                        No upcoming events scheduled
                      </Typography>
                      <Button
                        variant="contained"
                        sx={{ bgcolor: "#6200ee" }}
                        onClick={() => navigate("/publish-event")}
                      >
                        Publish Event
                      </Button>
                    </Box>
                  ) : (
                    <>
                      <Grid container spacing={2}>
                        {events.slice(0, isMobile ? 1 : 2).map((event) => (
                          <Grid item xs={12} key={event._id}>
                            <motion.div
                              variants={cardVariants}
                              initial="hidden"
                              animate="visible"
                              whileHover="hover"
                            >
                              <Card
                                sx={{
                                  p: 2,
                                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                                  bgcolor: "#fff",
                                  borderRadius: "10px",
                                  cursor: "pointer",
                                }}
                                onClick={() =>
                                  navigate("/event-details", {
                                    state: { event },
                                  })
                                }
                              >
                                <CardContent>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      mb: 1,
                                    }}
                                  >
                                    <EventIcon
                                      sx={{ color: "#6200ee", mr: 1 }}
                                    />
                                    <Typography
                                      variant="h6"
                                      sx={{
                                        fontWeight: "600",
                                        color: "#2c3e50",
                                      }}
                                    >
                                      {event.eventName}
                                    </Typography>
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: "#666", mb: 1 }}
                                  >
                                    <strong>Date:</strong>{" "}
                                    {new Date(
                                      event.dateTime
                                    ).toLocaleDateString()}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: "#666", mb: 1 }}
                                  >
                                    <strong>Venue:</strong> {event.venue}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: "#666" }}
                                  >
                                    <strong>Description:</strong>{" "}
                                    {event.description.substring(0, 60)}...
                                  </Typography>
                                </CardContent>
                              </Card>
                            </motion.div>
                          </Grid>
                        ))}
                      </Grid>

                      <Box sx={{ textAlign: "center", mt: 2 }}>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() =>
                            navigate("/all-events", {
                              state: { schoolId, userType: "institution" },
                            })
                          }
                          sx={{
                            borderRadius: "8px",
                            textTransform: "none",
                            px: 4,
                          }}
                        >
                          View All Events
                        </Button>
                      </Box>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6} data-aos="fade-up">
              {/* News & Announcements */}
              <Card
                sx={{
                  mb: 3,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                  borderRadius: "12px",
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 2,
                      justifyContent: "space-between",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <NewsIcon
                        sx={{
                          color: "white",
                          mr: 1,
                          bgcolor: "#6200ee",
                          p: 1,
                          borderRadius: "8px",
                          fontSize: 28,
                        }}
                      />
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: "bold", color: "#2c3e50" }}
                      >
                        News & Announcements
                      </Typography>
                    </Box>
                    <IconButton
                      color="primary"
                      onClick={() => navigate("/publish-news")}
                      sx={{
                        bgcolor: "#6200ee",
                        color: "white",
                        "&:hover": { bgcolor: "#7f39fb" },
                        borderRadius: "8px",
                      }}
                    >
                      <NewsIcon />
                    </IconButton>
                  </Box>

                  {news.length === 0 ? (
                    <Box
                      sx={{
                        textAlign: "center",
                        p: 4,
                        bgcolor: "#f8f9fa",
                        borderRadius: "8px",
                      }}
                    >
                      <img
                        src="https://cdn-icons-png.flaticon.com/512/3652/3652191.png"
                        alt="No news"
                        style={{ width: 100, opacity: 0.7, marginBottom: 16 }}
                      />
                      <Typography sx={{ color: "#666", mb: 2 }}>
                        No news or announcements yet
                      </Typography>
                      <Button
                        variant="contained"
                        sx={{ bgcolor: "#6200ee" }}
                        onClick={() => navigate("/publish-news")}
                      >
                        Publish News
                      </Button>
                    </Box>
                  ) : (
                    <>
                      <Grid container spacing={2}>
                        {news.slice(0, isMobile ? 1 : 2).map((item) => (
                          <Grid item xs={12} key={item._id}>
                            <motion.div
                              variants={cardVariants}
                              initial="hidden"
                              animate="visible"
                              whileHover="hover"
                            >
                              <Card
                                sx={{
                                  p: 2,
                                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                                  bgcolor: "#fff",
                                  borderRadius: "10px",
                                  cursor: "pointer",
                                }}
                                onClick={() =>
                                  navigate("/news-details", {
                                    state: { news: item },
                                  })
                                }
                              >
                                <CardContent>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      mb: 1,
                                    }}
                                  >
                                    <NewsIcon
                                      sx={{ color: "#6200ee", mr: 1 }}
                                    />
                                    <Typography
                                      variant="h6"
                                      sx={{
                                        fontWeight: "600",
                                        color: "#2c3e50",
                                      }}
                                    >
                                      {item.title}
                                    </Typography>
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: "#666" }}
                                  >
                                    {item.details.substring(0, 100)}...
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: "#999",
                                      display: "block",
                                      mt: 1,
                                    }}
                                  >
                                    Published:{" "}
                                    {new Date(
                                      item.createdAt
                                    ).toLocaleDateString()}
                                  </Typography>
                                </CardContent>
                              </Card>
                            </motion.div>
                          </Grid>
                        ))}
                      </Grid>

                      <Box sx={{ textAlign: "center", mt: 2 }}>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() =>
                            navigate("/all-news", { state: { schoolId } })
                          }
                          sx={{
                            borderRadius: "8px",
                            textTransform: "none",
                            px: 4,
                          }}
                        >
                          View All News
                        </Button>
                      </Box>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Third Row - National Events and Scholarships */}
            <Grid item xs={12} md={6} data-aos="fade-up">
              {/* National Events */}
              <Card
                sx={{
                  mb: 3,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                  borderRadius: "12px",
                }}
              >
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <PublicIcon
                      sx={{
                        color: "white",
                        mr: 1,
                        bgcolor: "#6200ee",
                        p: 1,
                        borderRadius: "8px",
                        fontSize: 28,
                      }}
                    />
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: "bold", color: "#2c3e50" }}
                    >
                      National Events
                    </Typography>
                  </Box>

                  {nationalEvents.length === 0 ? (
                    <Box
                      sx={{
                        textAlign: "center",
                        p: 4,
                        bgcolor: "#f8f9fa",
                        borderRadius: "8px",
                      }}
                    >
                      <img
                        src="https://cdn-icons-png.flaticon.com/512/3132/3132693.png"
                        alt="No national events"
                        style={{ width: 100, opacity: 0.7, marginBottom: 16 }}
                      />
                      <Typography sx={{ color: "#666" }}>
                        No national events available
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <Grid container spacing={2}>
                        {nationalEvents
                          .slice(0, isMobile ? 1 : 2)
                          .map((event) => (
                            <Grid item xs={12} key={event._id}>
                              <motion.div
                                variants={cardVariants}
                                initial="hidden"
                                animate="visible"
                                whileHover="hover"
                              >
                                <Card
                                  sx={{
                                    p: 2,
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                                    bgcolor: "#fff",
                                    borderRadius: "10px",
                                    cursor: "pointer",
                                  }}
                                  onClick={() =>
                                    navigate("/amplified-event-details", {
                                      state: { event },
                                    })
                                  }
                                >
                                  <CardContent>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        mb: 1,
                                      }}
                                    >
                                      <StarIcon
                                        sx={{ color: "#6200ee", mr: 1 }}
                                      />
                                      <Typography
                                        variant="h6"
                                        sx={{
                                          fontWeight: "600",
                                          color: "#2c3e50",
                                        }}
                                      >
                                        {event.eventName}
                                      </Typography>
                                    </Box>
                                    <Typography
                                      variant="body2"
                                      sx={{ color: "#666", mb: 1 }}
                                    >
                                      <strong>Date:</strong>{" "}
                                      {new Date(
                                        event.dateTime
                                      ).toLocaleDateString()}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ color: "#666", mb: 1 }}
                                    >
                                      <strong>Venue:</strong> {event.venue}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ color: "#666" }}
                                    >
                                      <strong>Organizer:</strong>{" "}
                                      {event.organizer}
                                    </Typography>
                                  </CardContent>
                                </Card>
                              </motion.div>
                            </Grid>
                          ))}
                      </Grid>

                      {nationalEvents.length > 0 && (
                        <Box sx={{ textAlign: "center", mt: 2 }}>
                          <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => navigate("/all-amplified-events")}
                            sx={{
                              borderRadius: "8px",
                              textTransform: "none",
                              px: 4,
                            }}
                          >
                            View All National Events
                          </Button>
                        </Box>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6} data-aos="fade-up">
              {/* Scholarships & Opportunities */}
              <Card
                sx={{
                  mb: 3,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                  borderRadius: "12px",
                }}
              >
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <EmojiEventsIcon
                      sx={{
                        color: "white",
                        mr: 1,
                        bgcolor: "#6200ee",
                        p: 1,
                        borderRadius: "8px",
                        fontSize: 28,
                      }}
                    />
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: "bold", color: "#2c3e50" }}
                    >
                      Scholarships & Opportunities
                    </Typography>
                  </Box>

                  {scholarships.length === 0 ? (
                    <Box
                      sx={{
                        textAlign: "center",
                        p: 4,
                        bgcolor: "#f8f9fa",
                        borderRadius: "8px",
                      }}
                    >
                      <img
                        src="https://cdn-icons-png.flaticon.com/512/3132/3132693.png"
                        alt="No scholarships"
                        style={{ width: 100, opacity: 0.7, marginBottom: 16 }}
                      />
                      <Typography sx={{ color: "#666" }}>
                        No scholarships available
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <Grid container spacing={2}>
                        {scholarships
                          .slice(0, isMobile ? 1 : 2)
                          .map((scholarship) => (
                            <Grid item xs={12} key={scholarship._id}>
                              <motion.div
                                variants={cardVariants}
                                initial="hidden"
                                animate="visible"
                                whileHover="hover"
                              >
                                <Card
                                  sx={{
                                    p: 2,
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                                    bgcolor: "#fff",
                                    borderRadius: "10px",
                                    cursor: "pointer",
                                  }}
                                  onClick={() =>
                                    navigate("/scholarship-details", {
                                      state: { scholarship },
                                    })
                                  }
                                >
                                  <CardContent>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        mb: 1,
                                      }}
                                    >
                                      <WorkspacesIcon
                                        sx={{ color: "#6200ee", mr: 1 }}
                                      />
                                      <Typography
                                        variant="h6"
                                        sx={{
                                          fontWeight: "600",
                                          color: "#2c3e50",
                                        }}
                                      >
                                        {scholarship.title}
                                      </Typography>
                                    </Box>
                                    <Typography
                                      variant="body2"
                                      sx={{ color: "#666", mb: 1 }}
                                    >
                                      <strong>Type:</strong> {scholarship.type}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ color: "#666", mb: 1 }}
                                    >
                                      <strong>Organization:</strong>{" "}
                                      {scholarship.hostOrganization}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ color: "#666" }}
                                    >
                                      <strong>Deadline:</strong>{" "}
                                      {new Date(
                                        scholarship.deadline
                                      ).toLocaleDateString()}
                                    </Typography>
                                  </CardContent>
                                </Card>
                              </motion.div>
                            </Grid>
                          ))}
                      </Grid>

                      {scholarships.length > 0 && (
                        <Box sx={{ textAlign: "center", mt: 2 }}>
                          <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => navigate("/all-scholarships")}
                            sx={{
                              borderRadius: "8px",
                              textTransform: "none",
                              px: 4,
                            }}
                          >
                            View All Scholarships
                          </Button>
                        </Box>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Mobile Bottom Navigation */}
      {isMobile && (
        <Paper
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: theme.zIndex.appBar,
            display: "flex",
            justifyContent: "space-around",
            alignItems: "center",
            py: 1,
            px: 0,
            borderTop: "1px solid #eee",
            borderRadius: 0,
          }}
          elevation={3}
        >
          {[
            {
              icon: <HomeIcon />,
              label: "Home",
              action: () => navigate("/dashboard"),
            },
            {
              icon: <EventIcon />,
              label: "Events",
              action: () =>
                navigate("/all-events", {
                  state: { schoolId, userType: "institution" },
                }),
            },
            {
              icon: <AssessmentIcon />,
              label: "Reports",
              action: () => navigate("/school-performance"),
            },
            { icon: <MenuIcon />, label: "Menu", action: handleDrawerToggle },
          ].map((item, index) => (
            <Button
              key={index}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                minWidth: "auto",
                color: location.pathname === item.path ? "#6200ee" : "#666",
                textTransform: "none",
                p: 1,
              }}
              onClick={item.action}
            >
              <IconButton sx={{ color: "inherit" }}>{item.icon}</IconButton>
              <Typography variant="caption" sx={{ fontSize: "0.65rem" }}>
                {item.label}
              </Typography>
            </Button>
          ))}
        </Paper>
      )}
    </Box>
  );
};

export default Dashboard;
